import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import transaction from '../../utils/api/transaction';

interface TransactionSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
}

interface TransactionDataProperties {
  _id: string;
  chargingPointSn: string;
  connector: number;
  status: string;
  dynamicPricing: {
    pricingType: string;
    price: string;
  };
  initialChargePercentage: string;
  desiredChargePercentage: string;
  batteryCapacity: string;
  chargingStation: any[]; // if you have its shape, you can define it better
  stripeCustomerObjectId: string;
  authorizePaymentIntentObjectId: string;
  paymentIntentObjectId: string;
  userCustomId: string;
  accountCustomId: string;
  isGuestUser: boolean;
  createdAt: string;
  updatedAt: string;
  meterStart: string;
  meterStop: string;
  transactionId: number;
  transactionStartsAt: string;
  transactionEndsAt: string;
  transactionEndReason: string;
  walletHistoryId: string;
  isFreeTransaction: boolean;
  isTransactionIncompleteDueToOffline: boolean;
  rfidNo: string;

  sampledData: {
    measurand: string;
    context: string;
    value: string;
    unit: string;
  };

  walletHistory: {
    _id: string;
    type: string;
    amount: string;
    stripeCustomer: string;
    currency: string;
    userCustomId: string;
    isGuestUser: boolean;
    createdAt: string;
    updatedAt: string;
    __v: number;
  };

  stripeCustomer: {
    _id: string;
    stripeCustomerId: string;
    paymentMethodId: string;
    cardHolderName: string;
    funding: string;
    brand: string;
    last4: number;
    expiryMonth: number;
    expiryYear: number;
    userCustomId: string;
    isGuestUser: boolean;
    isSelected: boolean;
    deletedAt: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
  };

  stripeAuthorizePaymentInfo: {
    _id: string;
    paymentIntendId: string;
    stripeCustomerObjectId: string;
    amount: string;
    currency: string;
    status: string;
    userCustomId: string;
    isGuestUser: boolean;
    createdAt: string;
    updatedAt: string;
    __v: number;
  };

  stripePaymentInfo: {
    _id: string;
    paymentIntendId: string;
    stripeCustomerObjectId: string;
    amount: string;
    currency: string;
    status: string;
    userCustomId: string;
    isGuestUser: boolean;
    createdAt: string;
    updatedAt: string;
    __v: number;
  };

  chargingSchedule: {
    _id: string;
    chargingPointSn: string;
    vehicleId: string;
    connectorId: number;
    startDateUtc: string;
    endDateUtc: string;
    userCustomId: string;
    accountCustomId: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
  };
}

/**
 * Station side panel
 * @param {any} properties Component properties
 * @returns {any} transaction side panel
 */
function TransactionSidePanel(properties: TransactionSidePanelProperties) {
  const { t } = useTranslation('sidePanel');
  const { fetchIdFrom, id } = properties;

  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  /* State to track window height for adaptive layout */
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [transactionData, setTransactionData] = useState<TransactionDataProperties>();
  /**
   * getPenaltyModeLabel
   * @param {any} mode penalty mode
   * @returns {string} penalty mode label
   */
  const getPenaltyModeLabel = (mode: string) => {
    switch (mode) {
      case 'grace':
        return t('grace');
      case 'flatRate':
        return t('flatRate');
      case 'perMinute':
        return t('perMinute');
      default:
        return '—';
    }
  };

  /**
   * ceilingTypeMap
   */
  const ceilingTypeMap: Record<string, string> = {
    fixedAmount: t('fixedAmount'),
    percentage: t('percentage'),
  };

  const transactionMap = [
    {
      header: t('transactionDetails'),
      keyValuePair: [
        { label: t('chargingPointSn'), key: 'chargingPointSn' },
        { label: t('connector'), key: 'connector' },
        { label: t('status'), key: 'status' },
        { label: t('transactionStartsAt'), key: 'transactionStartsAt', date: true },
        { label: t('transactionEndsAt'), key: 'transactionEndsAt', date: true },
        { label: t('transactionEndReason'), key: 'transactionEndReason' },
        { label: t('initialChargePercentage'), key: 'initialChargePercentage' },
        { label: t('desiredChargePercentage'), key: 'desiredChargePercentage' },
        { label: t('batteryCapacity'), key: 'batteryCapacity' },
        { label: t('meterStart'), key: 'meterStart' },
        { label: t('meterStop'), key: 'meterStop' },
        { label: t('isFreeTransaction'), key: 'isFreeTransaction' },
        {
          label: t('isTransactionIncompleteDueToOffline'),
          key: 'isTransactionIncompleteDueToOffline',
        },
        {
          label: t('rfidNo'),
          key: 'rfidNo',
        },
      ],
    },
    {
      header: t('pricingInfo'),
      keyValuePair: [
        { label: t('dynamicPricingType'), key: 'dynamicPricing.pricingType' },
        {
          label: t('dynamicPrice'),
          key: 'dynamicPricing.price',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('dynamicPrice')}</h2>
              <p>{ToFixedValue(value?.dynamicPricing?.price)}</p>
            </div>
          ),
        },
        {
          label: t('utilityUnitCost'),
          key: 'revenue.electricityCostByUtilityProvider',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */ view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('utilityUnitCost')}</h2>
              <p>{ToFixedValue(value?.revenue?.electricityCostByUtilityProvider)}</p>
            </div>
          ),
        },
        {
          label: t('totalUtilityCost'),
          key: 'revenue.energyCost',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */ view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('totalUtilityCost')}</h2>
              <p>{ToFixedValue(value?.revenue?.energyCost)}</p>
            </div>
          ),
        },
      ],
    },
    {
      header: t('idleStatePenalty'),
      keyValuePair: [
        {
          label: t('totalIdleMinutes'),
          key: 'penaltyDetails.totalMinutes',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('totalIdleMinutes')}</h2>
              <p>
                {value?.penaltyDetails?.totalMinutes
                  ? `${value.penaltyDetails.totalMinutes} min`
                  : '—'}
              </p>
            </div>
          ),
        },
        {
          label: t('maxCeilingType'),
          key: 'penaltyDetails.ceilingType',
          /**
           * maxCeilingType
           * @param {any} value row value
           * @returns {any} custom element
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('maxCeilingType')}</h2>
              <p>{ceilingTypeMap[value?.penaltyDetails?.ceilingType] || '—'}</p>
            </div>
          ),
        },

        {
          label: t('maxCeilingValue'),
          key: 'penaltyDetails.ceilingValue',
        },
        {
          label: t('graceTimeLimit'),
          key: 'penaltyDetails.graceTime',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('graceTimeLimit')}</h2>
              <p>
                {value?.penaltyDetails?.graceTime ? `${value.penaltyDetails.graceTime} min` : '—'}
              </p>
            </div>
          ),
        },

        /* ✅ Dynamic duration slabs */
        {
          label: t('idlePenaltySlabs'),
          key: 'penaltyDetails.durationAmountList',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */
          view: (value: any) => {
            const list = value?.penaltyDetails?.durationAmountList;

            if (!Array.isArray(list) || list.length === 0) {
              return (
                <div className="transaction-item">
                  <h2>{t('idlePenaltySlabs')}</h2>
                  <p>—</p>
                </div>
              );
            }

            return (
              <div className="transaction-item">
                {list.map((item: any, index: number) => {
                  return (
                    <>
                      <div key={item._id || index}>
                        <h2 style={{ flex: 1 }}>
                          {item.from} min – {item.to} min ({getPenaltyModeLabel(item.mode)})
                        </h2>
                      </div>
                      <div key={item._id || index}>
                        <p>{item.amount} </p>
                      </div>
                    </>
                  );
                })}
              </div>
            );
          },
        },
      ],
    },

    {
      header: t('consumerPayment'),
      keyValuePair: [
        {
          label: t('totalPowerConsumed'),
          key: 'powerConsumed',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */ view: (value: any) => {
            const power = Number(value?.powerConsumed) / 1000;
            return (
              <div className="transaction-item">
                <h2>{t('totalPowerConsumed')}</h2>
                <p>{ToFixedValue(power, 3)}</p>
              </div>
            );
          },
        },
        {
          label: t('chargingAmount'),
          key: 'revenueDataObject.estAmount',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */ view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('chargingAmount')}</h2>
              <p>{ToFixedValue(value?.revenueDataObject?.estAmount)}</p>
            </div>
          ),
        },
        {
          label: t('platformFee'),
          key: 'revenue.platformFee',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */ view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('platformFee')}</h2>
              <p>{ToFixedValue(value?.revenue?.platformFee)}</p>
            </div>
          ),
        },
        {
          label: t('transactionAmount'),
          key: 'walletHistory.amount',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */ view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('transactionAmount')}</h2>
              <p>{ToFixedValue(value?.walletHistory?.amount)}</p>
            </div>
          ),
        },
      ],
    },
    {
      header: t('networkRevenue'),
      keyValuePair: [
        {
          label: t('platformFeeAmount'),
          key: 'revenue.platformAmount',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */ view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('platformFeeAmount')}</h2>
              <p>{ToFixedValue(value?.revenue?.platformAmount)}</p>
            </div>
          ),
        },
        {
          label: t('platformFeeRate'),
          key: 'revenue.platformFee',
          /**
           * Rendering custom style data
           * @param {any} value - The value
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('platformFeeRate')}</h2>
              <p>{ToFixedValue(value?.revenue?.platformFee)}</p>
            </div>
          ),
        },
        {
          label: t('platformFeeType'),
          key: 'revenue.platformFee',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('platformFeeType')}</h2>
              <p>
                {value?.revenue?.platformFeeType === 'percentage' ? t('percentage') : t('fixed')}
              </p>
            </div>
          ),
        },
        {
          label: t('sessionFeeAmount'),
          key: 'revenue.sessionAmount',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('sessionFeeAmount')}</h2>
              <p>{ToFixedValue(value?.revenue?.sessionAmount)}</p>
            </div>
          ),
        },
        {
          label: t('sessionFeeRate'),
          key: 'revenue.sessionFee',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('sessionFeeRate')}</h2>
              <p>{ToFixedValue(value?.revenue?.sessionFee)}</p>
            </div>
          ),
        },
        {
          label: t('sessionFeeType'),
          key: 'revenue.sessionFee',
          /**
           * Theme set function
           * @param {any} value row value
           * @returns {any} custom element
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('sessionFeeType')}</h2>
              <p>
                {value?.revenue?.sessionFeeType === 'percentage' ? t('percentage') : t('fixed')}
              </p>
            </div>
          ),
        },
        {
          label: t('networkRevenue'),
          key: 'revenue.networkRevenue',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('networkRevenue')}</h2>
              <p>{ToFixedValue(value?.revenue?.networkRevenue)}</p>
            </div>
          ),
        },
      ],
    },
    {
      header: t('revenueShare'),
      keyValuePair: [
        {
          label: t('sharableRevenue'),
          key: 'revenue.sharableRevenue',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('sharableRevenue')}</h2>
              <p>{ToFixedValue(value?.revenue?.sharableRevenue)}</p>
            </div>
          ),
        },
        {
          label: t('adminRevenueAmount'),
          key: 'revenue.adminRevenue',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('adminRevenueAmount')}</h2>
              <p>{ToFixedValue(value?.revenue?.adminRevenue)}</p>
            </div>
          ),
        },
        {
          label: t('foRevenuePercentage'),
          key: 'revenue.revenuePercentage',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('foRevenuePercentage')}</h2>
              <p>
                {value?.revenue?.revenuePercentage ? `${value?.revenue?.revenuePercentage} %` : '—'}
              </p>
            </div>
          ),
        },
        {
          label: t('foRevenue'),
          key: 'revenue.foRevenue',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('foRevenue')}</h2>
              <p>{ToFixedValue(value?.revenue?.foRevenue)}</p>
            </div>
          ),
        },
        {
          label: t('foProfit'),
          key: 'revenueDataObject.foProfit',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('foProfit')}</h2>
              <p>{ToFixedValue(value?.revenueDataObject?.foProfit)}</p>
            </div>
          ),
        },
      ],
    },
    {
      header: t('sampledData'),
      keyValuePair: [
        { label: t('measurand'), key: 'sampledData.measurand' },
        { label: t('context'), key: 'sampledData.context' },
        {
          label: t('value'),
          key: 'sampledData.value',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('value')}</h2>
              <p>{ToFixedValue(value?.sampledData?.value)}</p>
            </div>
          ),
        },
        { label: t('unit'), key: 'sampledData.unit' },
      ],
    },
    {
      header: t('walletHistory'),
      keyValuePair: [
        { label: t('walletHistoryId'), key: 'walletHistory._id' },
        { label: t('type'), key: 'walletHistory.type' },
        {
          label: t('amount'),
          key: 'walletHistory.amount',
          /**
           * Rendering custom style data
           * @param {any} value row value
           * @returns {any} custom element
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('amount')}</h2>
              <p>{ToFixedValue(value?.walletHistory?.amount)}</p>
            </div>
          ),
        },
        { label: t('currency'), key: 'walletHistory.currency' },
        // { label: t('stripeCustomerRef'), key: 'walletHistory.stripeCustomer' },
        { label: t('userCustomId'), key: 'walletHistory.userCustomId' },
        { label: t('isGuestUser'), key: 'walletHistory.isGuestUser' },
        { label: t('createdAt'), key: 'walletHistory.createdAt', date: true },
      ],
    },
    // {
    //   header: t('stripeCustomer'),
    //   keyValuePair: [
    //     { label: t('stripeCustomerObjectId'), key: 'stripeCustomer._id' },
    //     { label: t('stripeCustomerId'), key: 'stripeCustomer.stripeCustomerId' },
    //     { label: t('paymentMethodId'), key: 'stripeCustomer.paymentMethodId' },
    //     { label: t('cardHolderName'), key: 'stripeCustomer.cardHolderName' },
    //     { label: t('funding'), key: 'stripeCustomer.funding' },
    //     { label: t('cardbrand'), key: 'stripeCustomer.brand' },
    //     { label: t('cardLast4Digits'), key: 'stripeCustomer.last4' },
    //     { label: t('expiryMonth'), key: 'stripeCustomer.expiryMonth' },
    //     { label: t('expiryYear'), key: 'stripeCustomer.expiryYear' },
    //     { label: t('userCustomId'), key: 'stripeCustomer.userCustomId' },
    //     { label: t('isGuestUser'), key: 'stripeCustomer.isGuestUser' },
    //     { label: t('isSelected'), key: 'stripeCustomer.isSelected' },
    //     { label: t('createdAt'), key: 'stripeCustomer.createdAt', date: true },
    //   ],
    // },
    // {
    //   header: t('stripeAuthorizedPayment'),
    //   keyValuePair: [
    //     { label: t('authorizedPaymentId'), key: 'stripeAuthorizePaymentInfo._id' },
    //     { label: t('paymentIntentId'), key: 'stripeAuthorizePaymentInfo.paymentIntendId' },
    //     { label: t('stripeCustomerRef'), key: 'stripeAuthorizePaymentInfo.stripeCustomerObjectId' },
    //     { label: t('amount'), key: 'stripeAuthorizePaymentInfo.amount' },
    //     { label: t('currency'), key: 'stripeAuthorizePaymentInfo.currency' },
    //     { label: t('status'), key: 'stripeAuthorizePaymentInfo.status' },
    //     { label: t('userCustomId'), key: 'stripeAuthorizePaymentInfo.userCustomId' },
    //     { label: t('isGuestUser'), key: 'stripeAuthorizePaymentInfo.isGuestUser' },
    //     { label: t('createdAt'), key: 'stripeAuthorizePaymentInfo.createdAt', date: true },
    //   ],
    // },
    // {
    //   header: t('stripeFinalPayment'),
    //   keyValuePair: [
    //     { label: t('finalPaymentId'), key: 'stripePaymentInfo._id' },
    //     { label: t('paymentIntentId'), key: 'stripePaymentInfo.paymentIntendId' },
    //     { label: t('stripeCustomerRef'), key: 'stripePaymentInfo.stripeCustomerObjectId' },
    //     { label: t('amount'), key: 'stripePaymentInfo.amount' },
    //     { label: t('currency'), key: 'stripePaymentInfo.currency' },
    //     { label: t('status'), key: 'stripePaymentInfo.status' },
    //     { label: t('userCustomId'), key: 'stripePaymentInfo.userCustomId' },
    //     { label: t('isGuestUser'), key: 'stripePaymentInfo.isGuestUser' },
    //     { label: t('createdAt'), key: 'stripePaymentInfo.createdAt', date: true },
    //   ],
    // },
    {
      header: t('chargingSchedule'),
      keyValuePair: [
        { label: t('scheduleId'), key: 'chargingSchedule._id' },
        { label: t('chargingPointSchedule'), key: 'chargingSchedule.chargingPointSn' },
        { label: t('vehicleId'), key: 'chargingSchedule.vehicleId' },
        { label: t('connectorId'), key: 'chargingSchedule.connectorId' },
        { label: t('startDate'), key: 'chargingSchedule.startDateUtc', date: true },
        { label: t('endDate'), key: 'chargingSchedule.endDateUtc', date: true },
        { label: t('userCustomId'), key: 'chargingSchedule.userCustomId' },
        { label: t('accountCustomId'), key: 'chargingSchedule.accountCustomId' },
        { label: t('status'), key: 'chargingSchedule.status' },
        { label: t('createdAt'), key: 'chargingSchedule.createdAt', date: true },
      ],
    },
    {
      header: t('userInfo'),
      keyValuePair: [
        { label: t('userCustomId'), key: 'userCustomId' },
        { label: t('accountCustomId'), key: 'accountCustomId' },
        { label: t('isGuestUser'), key: 'isGuestUser' },
      ],
    },
    {
      header: t('metaInfo'),
      keyValuePair: [
        { label: t('transactionObjectId'), key: '_id' },
        { label: t('walletHistoryRefId'), key: 'walletHistoryId' },
        // { label: t('stripeCustomerRefId'), key: 'stripeCustomerObjectId' },
        { label: t('authorizedPaymentRefId'), key: 'authorizePaymentIntentObjectId' },
        { label: t('finalPaymentRefId'), key: 'paymentIntentObjectId' },
        { label: t('createdAt'), key: 'createdAt', date: true },
      ],
    },
  ];

  /**
   * Function to fetch station details
   * @param {any} transactionId transaction id
   */
  const handleFetchStationDetails = async (transactionId: string) => {
    transaction
      .fetchTransactionById(transactionId)
      .then((response) => {
        setTransactionData(response?.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  /**
   * Function to get nested values from transactionData using dot notation
   * @param {any} path dot-separated key path
   * @param {any} utcToDate flag to convert to date
   * @returns {any} value or undefined
   */
  const getNestedValue = (path: string, utcToDate: boolean): any => {
    let value;
    try {
      value = path
        .split('.')
        .reduce((accumulator, key) => accumulator?.[key], transactionData as any);
      if (utcToDate) {
        value = `${ConvertUTCtoDate(value)} - ${FormatUtcString(ConvertUTCtoHHMM(value))}`;
      }
    } catch (error) {
      console.error(error);
    }
    return value;
  };

  useEffect(() => {
    let transactionObjectId;
    if (fetchIdFrom === 'queryParams') {
      transactionObjectId = searchParameters.get('transactionObjectId');
    } else if (fetchIdFrom === 'props') {
      transactionObjectId = id;
    }

    if (!transactionObjectId) {
      return;
    }

    handleFetchStationDetails(transactionObjectId);
  }, [searchParameters, id]);

  useEffect(() => {
    /**
     * Function to handle window resize
     * @returns {any} void
     */
    const handleResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="transaction-side-panel-wrap">
      <div
        className="transaction-side-panel-container"
        style={{
          height: `${windowHeight - 320}px`,
          overflowY: 'scroll',
        }}>
        {transactionMap.map((section) => (
          <div className="transaction-section">
            <h1>{section.header}</h1>
            {section.keyValuePair.map((item: any) => {
              const value = getNestedValue(item.key, item.date || false);
              return item.view ? (
                item.view(transactionData)
              ) : (
                <div className="transaction-item">
                  <h2>{item.label}: </h2>
                  <p>{value !== undefined && value !== null ? String(value) : '—'}</p>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export default TransactionSidePanel;
